import React, { useState } from 'react';
import { ChevronDown, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { products } from '../data/mockData';
import { useTranslation } from 'react-i18next';

const Catalog = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(products[0].id);
  const [expandedItems, setExpandedItems] = useState({});
  const [zoomedImage, setZoomedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [lastClickPos, setLastClickPos] = useState({ x: 0, y: 0 });

  const selectedProduct = products.find(p => p.id === selectedCategory);

  // Mapeo de nombres de tabs a claves de traducción
  const tabTranslationMap = {
    "Accesorios emparrado": t('catalog.tabs.wiringAccessories'),
    "Entutorado": t('catalog.tabs.tutoring'),
    "Atado": t('catalog.tabs.tying'),
    "Riego": t('catalog.tabs.irrigation'),
    "Herrajes": t('catalog.tabs.hardware')
  };

  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentSlide(0);
    setSelectedTab(0);
  };

  const nextSlide = (totalImages) => {
    setCurrentSlide((prev) => (prev + 1) % totalImages);
  };

  const prevSlide = (totalImages) => {
    setCurrentSlide((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const handleImageZoomClick = (e) => {
    e.stopPropagation();
    
    // Calcular distancia desde mouseDown hasta mouseUp
    const distance = Math.sqrt(
      Math.pow(e.clientX - lastClickPos.x, 2) + 
      Math.pow(e.clientY - lastClickPos.y, 2)
    );
    
    // Si hubo movimiento significativo (drag), no hacer zoom
    if (distance > 5) {
      return;
    }
    
    if (!isZoomedIn) {
      // Calcular la posición del clic relativa a la imagen
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      setZoomPosition({ x, y });
      setIsZoomedIn(true);
      setDragOffset({ x: 0, y: 0 }); // Reset offset
    } else {
      // Volver al zoom normal con un clic (sin drag)
      setIsZoomedIn(false);
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (isZoomedIn) {
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    }
    // Guardar posición del mouseDown para detectar si fue clic o drag
    setLastClickPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging && isZoomedIn) {
      e.stopPropagation();
      setDragOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      e.stopPropagation();
      setIsDragging(false);
    }
  };

  const handleCloseModal = () => {
    setZoomedImage(null);
    setIsZoomedIn(false);
    setZoomPosition({ x: 50, y: 50 });
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);
  };

  return (
    <section id="catalog" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-cmv-green font-semibold text-sm uppercase tracking-wider">
            {t('catalog.label')}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            {t('catalog.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('catalog.subtitle')}
          </p>
        </div>

        {/* Category tabs */}
        <div className="overflow-x-auto pb-4 mb-8 md:mb-12 -mx-4 px-4">
          <div className="flex md:flex-wrap md:justify-center gap-3 md:gap-4 min-w-max md:min-w-0">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => handleCategoryChange(product.id)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap text-sm md:text-base ${
                  selectedCategory === product.id
                    ? 'bg-cmv-green text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
              >
                {product.id === 1 ? t('catalog.categories.treatedWood') :
                 product.id === 2 ? t('catalog.categories.metalPosts') :
                 product.id === 3 ? t('catalog.categories.exteriorWood') :
                 product.id === 4 ? t('catalog.categories.accessories') :
                 product.category}
              </button>
            ))}
          </div>
        </div>

        {/* Selected category content */}
        {selectedProduct && (
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 lg:p-12 mb-6 md:mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                {selectedProduct.id === 1 ? t('catalog.categories.treatedWood') :
                 selectedProduct.id === 2 ? t('catalog.categories.metalPosts') :
                 selectedProduct.id === 3 ? t('catalog.categories.exteriorWood') :
                 selectedProduct.id === 4 ? t('catalog.categories.accessories') :
                 selectedProduct.category}
              </h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {selectedProduct.id === 1 ? t('catalog.descriptions.treatedWood') :
                 selectedProduct.id === 2 ? t('catalog.descriptions.metalPosts') :
                 selectedProduct.id === 3 ? t('catalog.descriptions.exteriorWood') :
                 selectedProduct.id === 4 ? t('catalog.descriptions.accessories') :
                 selectedProduct.description}
              </p>
            </div>

            {/* Check if it's a carousel type */}
            {selectedProduct.isCarousel ? (
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                {/* Carousel Container */}
                <div className="relative">
                  {/* Main Image Display */}
                  <div className="relative overflow-hidden rounded-lg bg-gray-50">
                    <div 
                      className="flex transition-transform duration-500 ease-out"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                      {selectedProduct.images.map((image, index) => (
                        <div key={index} className="w-full flex-shrink-0">
                          <img
                            src={image}
                            alt={`${selectedProduct.category} ${index + 1}`}
                            className="w-full h-96 object-cover cursor-pointer"
                            onClick={() => setZoomedImage({ src: image, alt: `${selectedProduct.category} ${index + 1}` })}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Navigation Arrows */}
                    <button
                      onClick={() => prevSlide(selectedProduct.images.length)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
                      aria-label="Imagen anterior"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={() => nextSlide(selectedProduct.images.length)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
                      aria-label="Siguiente imagen"
                    >
                      <ChevronRight size={24} />
                    </button>

                    {/* Zoom indicator */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
                      <ZoomIn size={16} />
                      <span>{t('catalog.clickToEnlarge')}</span>
                    </div>
                  </div>

                  {/* Dots Indicators */}
                  <div className="flex justify-center gap-2 mt-6">
                    {selectedProduct.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`transition-all duration-300 rounded-full ${
                          currentSlide === index
                            ? 'bg-cmv-green w-8 h-3'
                            : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                        }`}
                        aria-label={`Ir a imagen ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Solicitar Información Button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="px-6 md:px-8 py-3 md:py-4 bg-cmv-green text-white rounded-lg hover:bg-cmv-green-dark transition-colors font-semibold text-base md:text-lg"
                  >
                    {t('catalog.requestInfo')}
                  </button>
                </div>
              </div>
            ) : selectedProduct.hasTabs ? (
              /* Tabs with sliding content */
              <div>
                {/* Tabs Navigation */}
                <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                  <div className="relative flex items-center">
                    {/* Left Arrow */}
                    {selectedTab > 0 && (
                      <button
                        onClick={() => setSelectedTab(prev => Math.max(0, prev - 1))}
                        className="absolute left-0 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
                        aria-label="Pestaña anterior"
                      >
                        <ChevronLeft size={20} className="text-cmv-green" />
                      </button>
                    )}
                    
                    {/* Tabs Container */}
                    <div className="overflow-x-auto scrollbar-hide mx-8">
                      <div className="flex gap-2 md:gap-3 min-w-max">
                        {selectedProduct.tabs.map((tab, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedTab(index)}
                            className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap text-sm md:text-base ${
                              selectedTab === index
                                ? 'bg-cmv-green text-white shadow-lg'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {tabTranslationMap[tab.name] || tab.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Right Arrow */}
                    {selectedTab < selectedProduct.tabs.length - 1 && (
                      <button
                        onClick={() => setSelectedTab(prev => Math.min(selectedProduct.tabs.length - 1, prev + 1))}
                        className="absolute right-0 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
                        aria-label="Siguiente pestaña"
                      >
                        <ChevronRight size={20} className="text-cmv-green" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="space-y-4">
                  {selectedProduct.tabs[selectedTab].isImageGrid ? (
                    /* Image Grid for Accesorios */
                    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {selectedProduct.tabs[selectedTab].products.map((product, index) => (
                          <div key={index} className="relative group">
                            <div className="relative overflow-hidden rounded-lg bg-white border border-gray-200 hover:border-cmv-green transition-all duration-300">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-48 md:h-56 object-contain p-4 cursor-pointer transition-transform duration-300 group-hover:scale-110"
                                onClick={() => setZoomedImage({ src: product.image, alt: product.name })}
                              />
                              {/* Hover overlay with name */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                <div className="p-4 w-full">
                                  <p className="text-white font-semibold text-sm md:text-base text-center">
                                    {product.name}
                                  </p>
                                </div>
                              </div>
                              {/* Zoom icon */}
                              <div className="absolute top-2 right-2 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <ZoomIn size={16} className="text-cmv-green" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-8 text-center">
                        <button
                          onClick={() => {
                            const element = document.getElementById('contact');
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                          className="px-6 md:px-8 py-3 md:py-4 bg-cmv-green text-white rounded-lg hover:bg-cmv-green-dark transition-colors font-semibold text-base md:text-lg"
                        >
                          {t('catalog.requestInfo')}
                        </button>
                      </div>
                    </div>
                  ) : (
                  /* Regular items with accordion */
                  selectedProduct.tabs[selectedTab].items.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                    >
                      <button
                        onClick={() => toggleItem(`${selectedTab}-${index}`)}
                        className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 pr-4">
                          <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                            {item.name}
                          </h4>
                          <p className="text-sm md:text-base text-gray-600">{item.specs}</p>
                        </div>
                        <ChevronDown
                          className={`text-cmv-green transition-transform duration-300 flex-shrink-0 ${
                            expandedItems[`${selectedTab}-${index}`] ? 'rotate-180' : ''
                          }`}
                          size={24}
                        />
                      </button>
                      
                      {expandedItems[`${selectedTab}-${index}`] && (
                        <div className="px-4 md:px-6 pb-4 md:pb-6 border-t border-gray-100 pt-4 md:pt-6 animate-in slide-in-from-top-2 duration-300">
                          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                            <div className="relative group">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-40 md:h-48 object-contain rounded-lg bg-gray-50 cursor-pointer transition-transform duration-300 group-hover:scale-105"
                                onClick={() => setZoomedImage({ src: item.image, alt: item.name })}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                                <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
                              </div>
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900 mb-2 md:mb-3">{t('catalog.tableHeaders.applications')}:</h5>
                              <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">{item.applications}</p>
                              <button
                                onClick={() => {
                                  const element = document.getElementById('contact');
                                  if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}
                                className="w-full md:w-auto px-4 md:px-6 py-2 md:py-3 bg-cmv-green text-white rounded-lg hover:bg-cmv-green-dark transition-colors font-semibold text-sm md:text-base"
                              >
                                {t('catalog.requestInfo')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                  )}
                </div>
              </div>
            ) : (
              /* Product items */
              <div className="space-y-4">
              {selectedProduct.items.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                        {item.name}
                      </h4>
                      <p className="text-sm md:text-base text-gray-600">{item.specs}</p>
                    </div>
                    <ChevronDown
                      className={`text-cmv-green transition-transform duration-300 flex-shrink-0 ${
                        expandedItems[index] ? 'rotate-180' : ''
                      }`}
                      size={24}
                    />
                  </button>
                  
                  {expandedItems[index] && (
                    <div className="px-4 md:px-6 pb-4 md:pb-6 border-t border-gray-100 pt-4 md:pt-6 animate-in slide-in-from-top-2 duration-300">
                      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                        <div className="relative group">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-40 md:h-48 object-contain rounded-lg bg-gray-50 cursor-pointer transition-transform duration-300 group-hover:scale-105"
                            onClick={() => setZoomedImage({ src: item.image, alt: item.name })}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                            <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
                          </div>
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2 md:mb-3">{t('catalog.tableHeaders.applications')}:</h5>
                          <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">{item.applications}</p>
                          <button
                            onClick={() => {
                              const element = document.getElementById('contact');
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth' });
                              }
                            }}
                            className="w-full md:w-auto px-4 md:px-6 py-2 md:py-3 bg-cmv-green text-white rounded-lg hover:bg-cmv-green-dark transition-colors font-semibold text-sm md:text-base"
                          >
                            {t('catalog.requestInfo')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={handleCloseModal}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <button
            onClick={handleCloseModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors bg-black/50 rounded-full p-2 z-10"
            aria-label="Cerrar"
          >
            <X size={32} />
          </button>
          
          {/* Indicador de zoom */}
          <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 z-10">
            <ZoomIn size={16} />
            <span>
              {isZoomedIn 
                ? (t('catalog.dragToMove') || 'Arrastra para mover | Click para alejar')
                : (t('catalog.clickToZoomMore') || 'Click para más zoom')
              }
            </span>
          </div>
          
          <img
            src={zoomedImage.src}
            alt={zoomedImage.alt}
            className={`max-w-full max-h-[90vh] object-contain rounded-lg transition-all ${isDragging ? 'duration-0' : 'duration-300'}`}
            style={{
              transform: `scale(${isZoomedIn ? 2 : 1}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
              transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
              cursor: isZoomedIn ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
              userSelect: 'none'
            }}
            onClick={handleImageZoomClick}
            onMouseDown={handleMouseDown}
            draggable={false}
          />
        </div>
      )}
    </section>
  );
};

export default Catalog;
